/**
 * Small, quiet primitives shared by every Settings panel.
 *
 * These exist so a new config panel (chore rates, aisles, rulebook…) is a form
 * and a fetch, not a redesign. Everything here is token-only (src/styles.css),
 * 44px-minimum touch targets, real labels, visible focus.
 */
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90 disabled:bg-sage/50',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunk',
  ghost: 'text-ink-soft hover:bg-surface-sunk hover:text-ink',
  danger: 'bg-clay-soft text-clay border border-clay/30 hover:bg-clay/15',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${FOCUS} ${className}`}
    />
  );
}

/** Square 44px icon button — the reorder / row actions. */
export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      aria-label={label}
      title={label}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent ${FOCUS} ${className}`}
    />
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-border bg-surface ${className}`}>{children}</div>
  );
}

/** Status pill. Never color alone — the text carries the meaning. */
export function Pill({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'sage' | 'honey' | 'dusk' | 'clay';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-surface-sunk text-ink-soft',
    sage: 'bg-sage-soft text-sage',
    honey: 'bg-honey-soft text-honey',
    dusk: 'bg-dusk-soft text-dusk',
    clay: 'bg-clay-soft text-clay',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}

/** Label + hint + inline error, wired up with real ids. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="text-xs text-ink-soft">
          {hint}
        </p>
      )}
      {children({ id, 'aria-describedby': describedBy })}
      {error && (
        <p id={errorId} className="text-xs font-medium text-clay">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL =
  'min-h-11 w-full rounded-xl border border-border bg-surface px-3 text-base text-ink placeholder:text-ink-faint';

export function TextInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${FOCUS} ${className}`} />;
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${CONTROL} ${FOCUS} ${className}`} />;
}

/**
 * The alert for a *rejected action* — a 409 email_taken, a 403 last-adult, a 400
 * bad cap. It must be rendered INSIDE the thing the person is looking at: a
 * message painted on the page behind an aria-modal dialog is invisible to sighted
 * users and unreachable to a screen reader, so the save just looks like it did
 * nothing. Every dialog renders one of these at the top of its form.
 *
 * Inside a dialog it also takes focus (`focusOnShow`), so the rejection is both
 * announced and landed on rather than left below the fold of a scrolled form.
 */
export function AlertNote({
  message,
  focusOnShow = false,
}: {
  message: string;
  focusOnShow?: boolean;
}) {
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (focusOnShow) ref.current?.focus();
  }, [message, focusOnShow]);

  return (
    <p
      ref={ref}
      role="alert"
      tabIndex={-1}
      className="rounded-xl border border-clay/30 bg-clay-soft px-4 py-2 text-sm text-clay focus-visible:outline-none"
    >
      <span aria-hidden="true">⚠ </span>
      {message}
    </p>
  );
}

/** Error banner with a way out. Used for load failures and failed saves. */
export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-card border border-clay/30 bg-clay-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-clay">
        <span aria-hidden="true">⚠ </span>
        {message}
      </p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * Modal for add/edit. Escape closes, backdrop closes, focus lands on the first
 * field and is restored on close.
 */
export function Dialog({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  // onClose is a fresh closure on every render of the panel. Keeping it in a ref
  // lets the setup effect run ONCE, on mount. Re-running it would move focus back
  // to the first field on every re-render — including the render that shows a
  // rejection, which would rip focus straight off the alert.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const restoreTo = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button:not([data-dialog-close])'
    );
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreTo?.focus?.();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-card border border-border bg-surface p-5 shadow-xl sm:rounded-card"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="font-display text-xl font-semibold text-ink">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-1 text-sm text-ink-soft">
                {description}
              </p>
            )}
          </div>
          <IconButton label="Close" data-dialog-close onClick={onClose}>
            <span aria-hidden="true" className="text-lg leading-none">
              ✕
            </span>
          </IconButton>
        </div>
        {children}
      </div>
    </div>
  );
}
